import { lazy, Suspense, useState } from 'react';
import { PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWorkspace } from '@/features/workspaces';
import { useCanWork } from '@/features/workspaces/canWork';
import type {
  TaskRelationViewRelationType,
  TaskView,
} from '@/lib/api/generated/model';
import { useDirectTaskRelations } from '../hooks/useDirectTaskRelations';
import { useRelatedTasks } from '../hooks/useRelatedTasks';
import { useAddTaskRelation } from '../hooks/useAddTaskRelation';
import { useRemoveTaskRelation } from '../hooks/useRemoveTaskRelation';
import { useNavigate } from '@tanstack/react-router';

import { useRelatedTaskLink } from '../navigation';
import { relationLabel, type RelationRowModel } from '../types';
import { RelationRow } from './RelationRow';
import { TaskRelationPicker } from './TaskRelationPicker';
import { WiderWorkflowGroup } from './WiderWorkflowGroup';
import { PendingRows } from '@/shared/components/PendingRows';
import { ReadFailure } from '@/shared/components/ReadFailure';

/* React Flow is heavy and most readers never switch to the diagram. */
const RelationsCanvas = lazy(() =>
  import('./RelationsCanvas').then((module) => ({
    default: module.RelationsCanvas,
  })),
);

export interface TaskRelationsPanelProps {
  workspaceId: string;
  task: TaskView;
}

/**
 * Everything a task is connected to, in one place (spec 100).
 *
 * This replaces two surfaces: a read-only window opened from a menu, which could
 * show a task's whole neighbourhood but could not change any of it, and an
 * editable section buried below the fold of the Details tab, which could change
 * connections but could only ever see the nearest ones. Neither was wrong on its
 * own; having both was.
 *
 * The tab is offered for every task, including one with nothing connected,
 * because that is precisely the task someone opens it to add the first
 * connection to (FR-001, FR-014).
 */
export function TaskRelationsPanel({
  workspaceId,
  task,
}: TaskRelationsPanelProps) {
  const direct = useDirectTaskRelations(workspaceId, task.id);
  const workspace = useWorkspace(workspaceId);
  const canWork = useCanWork();
  const addRelation = useAddTaskRelation(workspaceId);
  const removeRelation = useRemoveTaskRelation(workspaceId);
  const relatedTaskLink = useRelatedTaskLink(workspaceId);
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /*
    Whether the reader has asked to see past their own connections. Component
    state, so leaving the tab puts it back to off along with every per-workflow
    sibling toggle (FR-024) — no effect, no cleanup, nothing to forget.

    It also gates the read: the transitive lookup is a graph traversal, and a
    reader who only glanced at the tab should never wait on it (FR-016, SC-004).
  */
  const [lookFurther, setLookFurther] = useState(false);
  const [view, setView] = useState<'list' | 'diagram'>('list');
  const wider = useRelatedTasks(workspaceId, task.id, lookFurther);

  const tasksById = new Map(
    (workspace.data?.tasks ?? []).map((other) => [other.id, other]),
  );

  const relations = direct.data?.relations ?? [];

  const rows: RelationRowModel[] = relations.map((relation) => {
    const isChild = relation.parent_task_id === task.id;
    const otherId = isChild ? relation.child_task_id : relation.parent_task_id;
    const otherTitle = isChild
      ? relation.child_task_title
      : relation.parent_task_title;
    const otherWorkflowId = isChild
      ? relation.child_task_workflow_id
      : relation.parent_task_workflow_id;
    return {
      key: relation.id,
      // Resolved directly on the relation itself — no lookup against a
      // separate, visibility-filtered task list, so a hidden or subtask
      // related task still shows its real title (spec 100 follow-up).
      title: otherTitle,
      context: null,
      label: relationLabel(relation.relation_type),
      link: relatedTaskLink(otherWorkflowId, otherId),
      distance: null,
      viaTitle: null,
      onRemove: !canWork
        ? null
        : () => {
            setFailure(null);
            removeRelation.mutate(
              {
                relationId: relation.id,
                taskIds: [relation.parent_task_id, relation.child_task_id],
              },
              { onError: (error: Error) => setFailure(error.message) },
            );
          },
    };
  });

  /* Siblings are per-group opt-ins held by each group's own component, so the
     diagram draws the groups and their chains; a sibling appears in the list
     where its toggle lives. */
  const widerForCanvas = (wider.data?.workflow_groups ?? []).map((group) => ({
    workflow_id: group.workflow_id,
    workflow_name: group.workflow_name,
    tasks: group.tasks,
  }));

  const widerGroups = (wider.data?.workflow_groups ?? []).filter(
    (group) =>
      group.tasks.some((item) => item.depth > 1) ||
      (group.siblings_available ?? 0) > 0,
  );

  /*
    Titles for naming the task a wider row is reached through. The transitive
    payload names the via task by id only, and every task it can name is
    somewhere in that same payload or in the workspace the drawer already read.
  */
  const titlesById = new Map<string, string>([
    ...[...tasksById].map(([id, other]) => [id, other.title] as const),
    ...(wider.data?.workflow_groups ?? []).flatMap((group) =>
      group.tasks.map((item) => [item.task_id, item.title] as const),
    ),
  ]);

  /* The same connections the list shows, in the shape the diagram's builder
     takes. One source, two presentations (FR-029). */
  const directEntries = relations.map((relation) => {
    const isChild = relation.parent_task_id === task.id;
    const otherId = isChild ? relation.child_task_id : relation.parent_task_id;
    const otherTitle = isChild
      ? relation.child_task_title
      : relation.parent_task_title;
    const otherWorkflowId = isChild
      ? relation.child_task_workflow_id
      : relation.parent_task_workflow_id;
    return {
      relationId: relation.id,
      relatedTask: {
        id: otherId,
        title: otherTitle,
        workflow_id: otherWorkflowId,
      },
      relationType: relation.relation_type,
      direction: isChild ? ('parent' as const) : ('child' as const),
    };
  });

  const workflowNames = new Map(
    (workspace.data?.workflows ?? []).map((w) => [w.id, w.name]),
  );

  const busy = addRelation.isPending || removeRelation.isPending;

  function removeById(relationId: string) {
    const relation = relations.find((candidate) => candidate.id === relationId);
    if (!relation) return;
    setFailure(null);
    removeRelation.mutate(
      {
        relationId,
        taskIds: [relation.parent_task_id, relation.child_task_id],
      },
      { onError: (error: Error) => setFailure(error.message) },
    );
  }

  function add(
    picked: TaskView,
    relationType: TaskRelationViewRelationType | undefined,
  ) {
    setFailure(null);
    setPickerOpen(false);
    addRelation.mutate(
      {
        parent_task_id: task.id,
        child_task_id: picked.id,
        relation_type: relationType,
      },
      { onError: (error: Error) => setFailure(error.message) },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/*
        List or diagram, both showing whatever is currently loaded — including
        the wider group once it has been asked for. A switch rather than a
        window over the drawer: room comes from the drawer's own wide mode
        (FR-029, FR-034).
      */}
      {/* On a phone the two share the row (spec 270): alone at its end
          they read as stray words. */}
      <div className="flex items-center justify-end gap-1 max-md:[&>button]:flex-1">
        {(['list', 'diagram'] as const).map((option) => (
          <Button
            key={option}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={view === option}
            // The same selected surface the kind chooser and the board's cards
            // use, so "picked" looks like one thing across the product.
            className={cn(
              'capitalize',
              view === option && 'bg-selected text-foreground',
            )}
            onClick={() => setView(option)}
          >
            {option}
          </Button>
        ))}
      </div>

      {view === 'diagram' ? (
        <Suspense
          fallback={
            <div className="border-border bg-muted/40 h-[28rem] w-full animate-pulse rounded-md border" />
          }
        >
          <RelationsCanvas
            current={{ id: task.id, title: task.title }}
            direct={directEntries}
            workflowNames={workflowNames}
            wider={lookFurther ? widerForCanvas : null}
            onSelect={(workflowId, taskId) => {
              const props = relatedTaskLink(workflowId, taskId);
              if (props) void navigate(props);
            }}
            onRemove={canWork ? removeById : undefined}
            isPending={busy}
          />
        </Suspense>
      ) : null}

      <section
        aria-labelledby="task-relations-direct"
        className="flex min-w-0 flex-col gap-2"
      >
        <div className="flex items-center justify-between gap-2">
          <h3
            id="task-relations-direct"
            className="text-foreground text-sm font-medium"
          >
            Connected directly
          </h3>
          {canWork ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              // The name this control has always had, kept so links, habits and
              // tests that name it keep working (spec 100 FR-044).
              aria-label="Add relation"
              disabled={busy}
              onClick={() => setPickerOpen(true)}
            >
              <PlusIcon aria-hidden="true" className="size-3.5" />
              Add
            </Button>
          ) : null}
        </div>

        {direct.isPending ? (
          <PendingRows label="Loading relations" />
        ) : direct.isError ? (
          // Before the empty state, never through it: a read that failed says
          // nothing about whether this task is connected (spec 126 FR-010).
          <ReadFailure
            message="The relations could not be loaded."
            onRetry={() => {
              void direct.refetch();
            }}
            isFetching={direct.isFetching}
          />
        ) : rows.length === 0 ? (
          /*
            The empty state carries the action (FR-014). The window this tab
            replaces said "No related tasks yet." and stopped there, which told a
            reader the room was empty without showing them the door.
          */
          <p className="text-muted-foreground text-sm">
            This task is not connected to anything yet.
          </p>
        ) : (
          <ul className="flex min-w-0 flex-col gap-1">
            {rows.map((row) => (
              <RelationRow key={row.key} row={row} />
            ))}
          </ul>
        )}

        {failure ? <p className="text-destructive text-sm">{failure}</p> : null}
      </section>

      {/*
        Everything beyond the task's own connections, under a heading that says
        so. Grouping by where a row came from is what makes one surface honest:
        a row here offers no remove control, and the heading explains why rather
        than leaving the reader to infer it from an absence (FR-017, FR-019).
      */}
      <section
        aria-labelledby="task-relations-wider"
        className="flex min-w-0 flex-col gap-2"
      >
        {/* On a phone the button takes its own full-width line rather than
            squeezing the heading onto two (spec 270). */}
        <div className="flex items-center justify-between gap-2 max-md:flex-wrap">
          <h3
            id="task-relations-wider"
            className="text-foreground text-sm font-medium"
          >
            Reached through other tasks
          </h3>
          {!lookFurther ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="max-md:basis-full"
              onClick={() => setLookFurther(true)}
            >
              Show indirect connections
            </Button>
          ) : null}
        </div>

        {!lookFurther ? (
          <p className="text-muted-foreground text-sm">
            Tasks linked to this one through others. Shown on request, because
            finding them follows the whole chain.
          </p>
        ) : wider.isPending ? (
          <p className="text-muted-foreground text-sm">Looking…</p>
        ) : wider.isError ? (
          <p className="text-muted-foreground text-sm">
            The wider view could not be loaded.
          </p>
        ) : widerGroups.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing is reached through another task.
          </p>
        ) : (
          <div className="flex min-w-0 flex-col gap-3">
            {widerGroups.map((group) => (
              <WiderWorkflowGroup
                key={group.workflow_id}
                workspaceId={workspaceId}
                taskId={task.id}
                group={group}
                titlesById={titlesById}
              />
            ))}
          </div>
        )}
      </section>

      {pickerOpen ? (
        <TaskRelationPicker
          workspaceId={workspaceId}
          excludeTaskIds={[
            task.id,
            ...relations.map((relation) =>
              relation.parent_task_id === task.id
                ? relation.child_task_id
                : relation.parent_task_id,
            ),
          ]}
          onSelect={add}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}
